import React, { useState, useRef } from 'react';
import { UploadIcon } from './icons/PlaybackIcons';

interface FileUploadProps {
  onFileUpload: (file: File) => Promise<void>;
  error?: string | null;
  label?: string;
  className?: string;
  buttonClassName?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({ 
  onFileUpload, 
  error, 
  label = "Upload File (.gpx, .tcx, .fit)",
  className = "",
  buttonClassName
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsUploading(true);
      try {
          // Await the actual upload/parse process passed from the parent
          await onFileUpload(file);
      } catch (e) {
          // Errors are handled via the 'error' prop usually, but we catch here to ensure loading state resets
          console.error("Upload failed in UI", e);
      } finally {
          setIsUploading(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = ""; // Reset input so same file can be selected again if needed
          }
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`w-full ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        disabled={isUploading}
        accept=".fit,.gpx,.tcx"
      />
      <button
        onClick={handleClick}
        disabled={isUploading}
        className={`w-full flex items-center justify-center rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            buttonClassName || 'px-8 py-3 text-lg'
        } ${
            error 
            ? 'bg-red-600 hover:bg-red-500 text-white shadow-[0_0_15px_rgba(220,38,38,0.3)]' 
            : 'bg-slate-600 hover:bg-slate-500 text-white shadow-[0_0_15px_rgba(71,85,105,0.3)]'
        }`}
      >
        {isUploading ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Uploading to Cloud...
          </>
        ) : (
          <>
            <UploadIcon className="w-5 h-5 mr-2" />
            {label}
          </>
        )}
      </button>
      {error && (
          <div className="mt-2 p-2 text-xs text-red-300 bg-red-900/40 border border-red-800 rounded flex items-start">
              <span className="mr-1 font-bold">Error:</span> {error}
          </div>
      )}
    </div>
  );
};

export default FileUpload;